import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Plug, X } from "lucide-react";
import { API, fmtDateTime } from "./utils";

/* ---------- Zoho Connect Card ---------- */
export default function ZohoConnectCard({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/zoho-webinar/status`, { headers: { "X-Admin-Token": token } });
      setStatus(r.data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/admin/zoho-webinar/connect/start`, { headers: { "X-Admin-Token": token } });
      window.location.href = r.data.consent_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Zoho-Consent-URL nicht generieren.");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Zoho-Verbindung wirklich trennen? Du musst danach neu autorisieren.")) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/admin/zoho-webinar/disconnect`, {}, { headers: { "X-Admin-Token": token } });
      toast.success("Zoho-Verbindung getrennt.");
      load();
    } catch (e) {
      toast.error("Trennen fehlgeschlagen.");
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <section className="al-card p-4 sm:p-5" data-testid="zoho-card-loading">
        <div className="flex items-center gap-3 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Zoho-Status prüfen …</div>
      </section>
    );
  }

  const connected = status?.connected;

  return (
    <section
      className={`al-card p-4 sm:p-5 border-2 ${connected ? "border-emerald-200 bg-gradient-to-br from-emerald-50/60 via-white to-white" : "border-violet-200 bg-gradient-to-br from-violet-50/40 via-white to-white"}`}
      data-testid="zoho-connect-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`h-10 w-10 rounded-xl flex-shrink-0 flex items-center justify-center ${connected ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-[var(--al-purple)]"}`}>
            {connected ? <CheckCircle2 className="h-5 w-5" /> : <Plug className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display font-bold text-base sm:text-lg">Zoho Webinar Integration</h3>
              <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${connected ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {connected ? "Verbunden" : "Nicht verbunden"}
              </span>
            </div>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              {connected ? (
                <>
                  Verbindung aktiv. Nutze pro Webinar die <strong>„Teilnahme syncen"</strong>- und <strong>„No-Show-Mail"</strong>-Buttons (sichtbar bei vergangenen Webinaren).
                </>
              ) : (
                <>
                  Verbinde dein Zoho-Webinar-Konto, damit Alphalane nach jedem Webinar automatisch die <strong>Teilnehmerliste abruft</strong> und Nicht-Teilnehmer per Follow-up-Mail reaktiviert.
                </>
              )}
            </p>
            {connected && status?.updated_at && (
              <p className="text-xs text-slate-400 mt-1 font-mono-tab">
                Token aktualisiert: {fmtDateTime(status.updated_at)}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {connected ? (
            <button
              onClick={disconnect}
              disabled={disconnecting}
              data-testid="zoho-disconnect"
              className="al-cta-ghost text-xs disabled:opacity-50"
            >
              {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              <span>Verbindung trennen</span>
            </button>
          ) : (
            <button
              onClick={connect}
              disabled={connecting}
              data-testid="zoho-connect"
              className="al-cta text-sm disabled:opacity-50"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
              <span>Mit Zoho verbinden</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
